/**
 * El riel del paso 3: lo que es dato del servidor, angosto, sin competir con
 * las tareas (handoff paso3-1b, 2026-09-06). Y la tarjeta de publicar, que es
 * la acción de cierre de la columna de trabajo.
 *
 * Las filas del riel van etiqueta arriba / valor abajo: el riel es angosto y la
 * fila de dos columnas de KVRow no cabe.
 */
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../../ui/DetailSection.js';
import { SectionHeaderAction } from '../../ui/SectionHeaderAction.js';
import { Pill } from '../../PillGroup.js';
import { useToast } from '../../ui/Toast.js';
import type { ProjectRow } from '../../../hooks/useProjects.js';
import { useProjectAudits, useRunAudit } from '../../../hooks/useProjects.js';
import { useSiteConnection } from '../../../hooks/useSiteConnection.js';

const menor = {
  fontSize: 'var(--evo-fs-tiny)',
  color: 'var(--evo-fg-4)',
  fontFamily: 'var(--evo-font)',
} as const;

const valor = {
  fontSize: 'var(--evo-fs-label)',
  color: 'var(--evo-fg-2)',
  fontFamily: 'var(--evo-font)',
  overflowWrap: 'anywhere',
} as const;

function FilaDelRiel({ etiqueta, children, ultima, apagado }: { etiqueta: string; children: React.ReactNode; ultima?: boolean; apagado?: boolean }) {
  return (
    <div style={{ padding: 'var(--evo-space-2) 0', borderBottom: ultima ? 'none' : '1px solid var(--evo-stroke)' }}>
      <div style={menor}>{etiqueta}</div>
      <div style={{ ...valor, color: apagado ? 'var(--evo-fg-4)' : 'var(--evo-fg-2)' }}>{children}</div>
    </div>
  );
}

export function RielSitioEnLinea({ project }: { project: ProjectRow }) {
  const { t, i18n } = useTranslation();
  const { data: connData } = useSiteConnection(project.id);
  const conn = connData?.connection ?? null;
  const s = 'projects.design.tab.step3';
  // Plegada: es dato, no tarea. La dirección se ve en la cabecera aunque esté cerrada.
  return (
    <DetailSection title={t(`${s}.onlineTitle`)} collapsible defaultOpen={false}>
      <FilaDelRiel etiqueta={t(`${s}.domain`)} apagado={!conn?.siteUrl}>
        {conn?.siteUrl
          ? (
            <a href={conn.siteUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--evo-cyan)', textDecoration: 'none' }}>
              {conn.siteUrl.replace(/^https?:\/\//, '')}
            </a>
          )
          : conn?.host ?? '—'}
      </FilaDelRiel>
      <FilaDelRiel etiqueta={t(`${s}.pagesPublished`)} apagado={connData?.paginas == null}>
        {connData?.paginas != null ? t(`${s}.pagesCount`, { count: connData.paginas }) : '—'}
      </FilaDelRiel>
      <FilaDelRiel etiqueta={t(`${s}.lastDeploy`)} apagado={!conn?.publishedAt}>
        {conn?.publishedAt ? new Date(conn.publishedAt).toLocaleString(i18n.language, { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
      </FilaDelRiel>
      <FilaDelRiel etiqueta={t(`${s}.server`)} apagado={!conn}>
        {conn ? `${conn.protocol}://${conn.host} · ${conn.remotePath}` : '—'}
      </FilaDelRiel>
      {/* Indexadas: lo que Google dice de los sitemaps enviados. Sin sitemap
          enviado o sin Search Console no hay dato, y se dice cuál de las dos
          es — nunca un guion mudo ni un cero. */}
      <FilaDelRiel etiqueta={t(`${s}.indexed`)} apagado={!connData?.indexadas} ultima>
        {connData?.indexadas
          ? t(`${s}.indexedCount`, { indexadas: connData.indexadas.indexadas, enviadas: connData.indexadas.enviadas })
          : connData?.indexadasPorque === 'sin-gsc'
            ? t(`${s}.indexedNoGsc`)
            : connData?.indexadasPorque === 'sin-sitemap'
              ? t(`${s}.indexedNone`)
              : '—'}
      </FilaDelRiel>
    </DetailSection>
  );
}

export function RielReportes({ project }: { project: ProjectRow }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { data: audits } = useProjectAudits(project.id);
  const runAudit = useRunAudit(project.id);
  const { data: connData } = useSiteConnection(project.id);
  const s = 'projects.design.tab.step3';

  // La auditoría del sitio PUBLICADO sigue disponible desde acá; la del
  // borrador la corre el panel de salud, que es donde se ve el resultado.
  function nuevo(): void {
    runAudit.mutate(connData?.connection?.siteUrl ? 'published' : 'local', {
      onError: err => { toast.error(err instanceof Error ? err.message : t('common.error')); },
    });
  }

  return (
    <DetailSection
      title={t(`${s}.reportsTitle`)}
      padding="none"
      collapsible
      defaultOpen={false}
      titleRight={<SectionHeaderAction loading={runAudit.isPending} onClick={nuevo}>{t(`${s}.newReport`)}</SectionHeaderAction>}
    >
      {(audits ?? []).length === 0
        ? <p style={{ ...menor, margin: 0, padding: 'var(--evo-space-3)' }}>{t(`${s}.reportsCount`, { count: 0 })}</p>
        : (audits ?? []).map((a, i) => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', padding: 'var(--evo-space-2) var(--evo-space-3)', borderBottom: i === (audits?.length ?? 0) - 1 ? 'none' : '1px solid var(--evo-stroke)' }}>
            <span style={{ ...valor, flex: 1 }}>v{String(a.targetVersion)}</span>
            <span style={menor}>{new Date(a.auditedAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })}</span>
            <Pill variant="outline" tone={(a.score ?? 0) >= 90 ? 'cyan' : (a.score ?? 0) >= 60 ? 'amber' : 'danger'} size="xs">
              {String(a.score ?? '—')}
            </Pill>
          </div>
        ))}
    </DetailSection>
  );
}
